/**
 * Assemble le « contexte client » envoyé par la tribune GOD au backend : tout
 * ce que la page review a effectivement RENDU (findings affichés, parcours,
 * KPI, liens des PJ) + le dump iaTrace best-effort. Le backend complète ensuite
 * avec le snapshot serveur (extractions complètes, findings figés, audit, trace
 * d'ingestion). Fonction PURE — testable hors React/jsdom.
 */

export interface FindingLite {
  ruleId: string;
  severity: 'error' | 'warning' | 'info';
  message: string | null;
  documentTypes: string[];
  fields: string[];
}

export interface PieceLite {
  id: number | null;
  imageUrl: string | null;
  docTypeDetected: string | null;
  confidence: number | null;
}

export interface GodFeedbackClientContext {
  regPlate: string | null;
  parcours: string | null;
  /**
   * Provenance du parcours (`agence` = case cochée à l'étape 1, sinon le moteur
   * qui l'a détecté, `null` = inconnue). Sans elle, un désaccord du type
   * « succession répertoriée alors que non » (GD-567-EJ) obligeait à remonter
   * le snapshot à la main pour savoir si l'IA était en cause.
   */
  parcoursSource: string | null;
  aiConfidence: number | null;
  alertFindings: FindingLite[];
  piecesFindings: FindingLite[];
  pieces: PieceLite[];
  /** Dump du buffer iaTrace si disponible (souvent vide sur la review). */
  iaTraceText: string | null;
}

interface FindingInput {
  ruleId: string;
  severity: 'error' | 'warning' | 'info';
  message?: string | null;
  documentTypes?: string[];
  fields?: string[];
}

interface ExtractionInput {
  id?: number;
  imageUrl?: string | null;
  docTypeDetected?: string | null;
  confidence?: number | null;
}

export interface BuildGodFeedbackContextArgs {
  regPlate: string | null;
  parcours: string | null;
  parcoursSource?: string | null;
  aiConfidence: number | null;
  alertFindings: FindingInput[];
  piecesFindings: FindingInput[];
  extractions: ExtractionInput[];
}

const toFindingLite = (f: FindingInput): FindingLite => ({
  ruleId: f.ruleId,
  severity: f.severity,
  message: f.message ?? null,
  documentTypes: f.documentTypes ?? [],
  fields: f.fields ?? [],
});

/** Lit le buffer iaTrace exposé sur `window` (best-effort, null hors navigateur). */
const readIaTraceText = (): string | null => {
  if (typeof window === 'undefined') return null;
  const dumper = (window as Window & { __formCgiaIaTrace?: { text?: () => string } }).__formCgiaIaTrace;
  const text = dumper?.text?.();
  return typeof text === 'string' && text.length > 0 ? text : null;
};

export const buildGodFeedbackClientContext = (
  args: BuildGodFeedbackContextArgs,
): GodFeedbackClientContext => ({
  regPlate: args.regPlate,
  parcours: args.parcours,
  parcoursSource: args.parcoursSource ?? null,
  aiConfidence: args.aiConfidence,
  alertFindings: args.alertFindings.map(toFindingLite),
  piecesFindings: args.piecesFindings.map(toFindingLite),
  pieces: args.extractions.map((e) => ({
    id: e.id ?? null,
    imageUrl: e.imageUrl ?? null,
    docTypeDetected: e.docTypeDetected ?? null,
    confidence: e.confidence ?? null,
  })),
  iaTraceText: readIaTraceText(),
});
